<template>
	<div ref="rootRef" class="relative inline-block shrink-0 text-left">
		<button
			v-if="icon"
			type="button"
			class="flex items-center justify-center p-1"
			:aria-label="currentLocaleLabel"
			@click="toggleOpen"
		>
			<img :src="currentFlag" :alt="currentLocaleLabel" class="h-4 w-[22px] rounded-sm object-cover" />
		</button>
		<template v-else>
			<button
				type="button"
				data-locale-trigger-full
				class="flex items-center gap-2 whitespace-nowrap rounded-[18px] border border-dj-red bg-white px-3 py-2 shadow-sm transition hover:border-dj-red/80"
				:class="compact ? 'invisible absolute pointer-events-none' : ''"
				:tabindex="compact ? -1 : 0"
				:aria-hidden="compact"
				@click="toggleOpen"
			>
				<img :src="currentFlag" :alt="currentLocaleLabel" class="h-4 w-[22px] rounded-sm object-cover" />
				<span class="text-base font-semibold text-dj-red uppercase">{{ currentLocaleCode }}</span>
				<span class="material-icons-outlined text-lg text-dj-red">expand_more</span>
			</button>
			<button
				type="button"
				data-locale-trigger-compact
				class="flex items-center gap-1 whitespace-nowrap rounded-xl border border-dj-red bg-white px-2 py-1 transition hover:border-dj-red/80"
				:class="compact ? '' : 'invisible absolute pointer-events-none'"
				:tabindex="compact ? 0 : -1"
				:aria-hidden="!compact"
				@click="toggleOpen"
			>
				<img :src="currentFlag" :alt="currentLocaleLabel" class="h-4 w-[22px] rounded-sm object-cover" />
				<span class="text-[11px] font-semibold text-dj-red uppercase">{{ currentLocaleCode }}</span>
				<span class="material-icons-outlined text-sm text-dj-red">expand_more</span>
			</button>
		</template>

		<div
			v-if="isOpen"
			class="absolute right-0 top-[calc(100%+8px)] z-50 min-w-[220px] rounded-[18px] border border-dj-gray-light bg-white p-2 shadow-xl"
		>
			<button
				type="button"
				v-for="item in localeOptions"
				:key="item.value"
				class="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-dj-gray-light/50"
				:class="item.value === modelValue ? 'bg-dj-gray-light/50' : ''"
				@click="selectLocale(item.value)"
			>
				<div class="flex items-center gap-3">
					<img :src="item.flag" :alt="item.label" class="h-4 w-[22px] rounded-sm object-cover" />
					<span class="text-base font-medium text-dj-black whitespace-nowrap">{{ item.label }}</span>
				</div>
				<span v-if="item.value === modelValue" class="h-2.5 w-2.5 rounded-full bg-dj-red"></span>
			</button>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from "vue";
import type { PropType } from "vue";
import { onClickOutside } from "@vueuse/core";

export default defineComponent({
	name: "BaseLocaleChanger",
	props: {
		modelValue: {
			type: String,
			default: "",
		},
		locales: {
			type: Array as PropType<Array<string>>,
			required: true,
			default: () => [""],
		},
		compact: {
			type: Boolean,
			default: false,
		},
		icon: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["update:modelValue", "onLocaleChange"],
	setup(props, { emit }) {
		const isOpen = ref(false);
		const rootRef = ref<HTMLElement | null>(null);

		const localeOptions = computed(() =>
			props.locales.map((locale) => ({
				value: locale,
				label: getLocaleLabel(locale),
				flag: getFlagPath(locale),
			}))
		);

		const currentLocaleCode = computed(() => (props.modelValue || "").toUpperCase());
		const currentLocaleLabel = computed(() => getLocaleLabel(props.modelValue));
		const currentFlag = computed(() => getFlagPath(props.modelValue));

		function getLocaleLabel(value: string) {
			switch (value) {
				case "hu":
					return "Magyar";
				case "ro":
					return "Română";
				case "en":
					return "English";
				default:
					return value.toUpperCase();
			}
		}

		function getFlagPath(value: string) {
			return `/flags/${value || "en"}.svg`;
		}

		function toggleOpen() {
			isOpen.value = !isOpen.value;
		}

		function selectLocale(value: string) {
			emit("update:modelValue", value);
			emit("onLocaleChange");
			isOpen.value = false;
		}

		onClickOutside(rootRef, () => {
			isOpen.value = false;
		});

		return {
			rootRef,
			isOpen,
			localeOptions,
			currentLocaleCode,
			currentLocaleLabel,
			currentFlag,
			toggleOpen,
			selectLocale,
		};
	},
});
</script>
